import { Component } from '@theme/component';
import { addLines } from '@theme/lea-cart';

/**
 * The buy panel for a catalogue whose separates are the only inventory record.
 *
 * Two products, two size choices, one add. The set is two cart lines from a
 * single `cart/add` `items[]` call, never a third SKU, because a synthetic set
 * product puts inventory in three places and oversells the moment one half runs
 * out.
 *
 * The panel is additive rather than modal: the piece she came for is the
 * primary action, and the matching half sits under it as an addition she can
 * take or leave. Nothing is hidden behind a state, so nothing jumps.
 */
class LeaSetBuilder extends Component {
  requiredRefs = ['submit'];

  /** @type {Record<string, HTMLButtonElement | null>} */
  picked = { main: null, partner: null };

  /** Once she chooses a size on the matching piece, stop mirroring over it. */
  partnerTouched = false;

  connectedCallback() {
    super.connectedCallback();
    this.#restoreSizes();
    this.#selectSingles();
    this.#sync();
    this.#resumeNotify();
  }

  /* ---------------------------------------------------------------- sizes */

  /**
   * An S on top and an M below is the norm on this catalogue, not an edge case,
   * so re-asking on every product is the single largest piece of friction in
   * the whole journey. The size is remembered per garment word, which is why
   * the top's answer never lands on a bottom.
   * @param {string} word
   * @param {string} label
   */
  #remember(word, label) {
    if (!word || !label) return;
    try {
      localStorage.setItem(`lea:size:${word}`, label);
    } catch {
      /* private windows and blocked storage are not errors here */
    }
  }

  /** @param {string} word */
  #recall(word) {
    try {
      return localStorage.getItem(`lea:size:${word}`);
    } catch {
      return null;
    }
  }

  #restoreSizes() {
    const roles = { main: this.dataset.pieceWord, partner: this.dataset.pairWord };
    for (const [role, word] of Object.entries(roles)) {
      const wanted = this.#recall(word || '');
      if (!wanted) continue;
      const chip = [...this.querySelectorAll(`[data-lea-variant="${role}"]`)].find(
        (b) => b instanceof HTMLButtonElement && b.dataset.sizeLabel === wanted && b.dataset.available === 'true'
      );
      if (chip instanceof HTMLButtonElement) this.#select(role, chip);
    }
  }

  /** True when a piece comes in one size only. */
  partnerSingle = false;

  /**
   * One size is not a choice, so the row is not drawn and its only chip is
   * selected here: the button reads "Add to bag" from the start, and the set
   * never asks her to pick the size it already knows.
   */
  #selectSingles() {
    for (const role of /** @type {const} */ (['main', 'partner'])) {
      const chips = this.querySelectorAll(`[data-lea-variant="${role}"]`);
      const only = chips[0];
      if (chips.length !== 1 || !(only instanceof HTMLButtonElement)) continue;
      this.picked[role] = only;
      only.setAttribute('aria-pressed', 'true');
      if (role === 'partner') {
        this.partnerTouched = true;
        this.partnerSingle = true;
      }
    }
  }

  /**
   * @param {'main' | 'partner'} role
   * @param {HTMLButtonElement} button
   */
  #select(role, button) {
    this.picked[role] = button;
    for (const other of this.querySelectorAll(`[data-lea-variant="${role}"]`)) {
      other.setAttribute('aria-pressed', String(other === button));
    }
    const word = role === 'main' ? this.dataset.pieceWord : this.dataset.pairWord;
    if (button.dataset.available === 'true') this.#remember(word || '', button.dataset.sizeLabel || '');
  }

  /* --------------------------------------------------------------- mirror */

  /**
   * Most shoppers are the same size top and bottom, so asking twice is asking a
   * question they do not have. Her answer is mirrored onto the matching piece
   * and the set becomes one button; the folded row of chips is there for the
   * 43% who are two sizes, and unfolds itself when the mirrored size is the one
   * that has sold out.
   */
  #mirror() {
    const main = this.picked.main;
    const slot = this.querySelector('[data-lea-mirror]');
    const fold = this.querySelector('[data-lea-partner-sizes]');
    const other = this.querySelector('[data-lea-othersize]');
    if (!(slot instanceof HTMLElement)) return;

    // A one-size partner has nothing to mirror and no other size to offer.
    if (this.partnerSingle) {
      slot.textContent = '';
      if (other instanceof HTMLElement) other.hidden = true;
      return;
    }

    if (!main) {
      slot.textContent = this.dataset.labelChooseMain ?? '';
      if (other instanceof HTMLElement) other.hidden = true;
      return;
    }
    if (other instanceof HTMLElement) other.hidden = false;

    const label = main.dataset.sizeLabel;
    const twin = [...this.querySelectorAll('[data-lea-variant="partner"]')].find(
      (b) => b instanceof HTMLButtonElement && b.dataset.sizeLabel === label
    );

    // Only mirror while she has not made her own choice on the partner.
    if (!this.partnerTouched && twin instanceof HTMLButtonElement && twin.dataset.available === 'true') {
      this.#select('partner', twin);
      slot.textContent = `${this.dataset.labelAlsoIn ?? 'Also in'} ${label}`;
      slot.dataset.tone = '';
      return;
    }

    if (!this.partnerTouched) {
      // Her size is gone on the other half. Say so, and open the row rather
      // than leaving her to discover it behind a link.
      this.picked.partner = null;
      slot.textContent = `${label} ${this.dataset.labelGone ?? 'is sold out here'}`;
      slot.dataset.tone = 'bad';
      if (fold instanceof HTMLElement) fold.hidden = false;
      if (other instanceof HTMLElement) other.setAttribute('aria-expanded', 'true');
      return;
    }

    const chosen = this.picked.partner;
    if (chosen) slot.textContent = `${this.dataset.labelAlsoIn ?? 'Also in'} ${chosen.dataset.sizeLabel}`;
  }

  /** Unfold the second size row, for the shopper who is two sizes. */
  otherSize = (event) => {
    const button = event.target;
    if (!(button instanceof HTMLElement)) return;
    const fold = this.querySelector('[data-lea-partner-sizes]');
    if (!(fold instanceof HTMLElement)) return;
    fold.hidden = !fold.hidden;
    button.setAttribute('aria-expanded', String(!fold.hidden));
  };

  /** @param {Event} event */
  pick = (event) => {
    // Horizon delegates every `on:click` from `document`, so `currentTarget` is
    // the document. Its proxy rewrites `target` to the bound element instead.
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    const role = button.dataset.leaVariant;
    if (role !== 'main' && role !== 'partner') return;

    if (role === 'partner') this.partnerTouched = true;
    this.#select(role, button);
    this.#clearMessage();
    this.#sync();
  };

  /* ---------------------------------------------------------------- adding */

  submit = async () => {
    const main = this.picked.main;
    if (!main) return this.#focusFirst('main');

    if (main.dataset.available === 'false') return this.#notify(main);

    await this.#add([main], this.refs.submit);
  };

  /**
   * Adds the matching piece and only the matching piece. The button says "Add
   * matching bottom", so that is what it must do: a control labelled with one
   * garment that quietly adds two is the thing that made "Add the set"
   * ambiguous in the first place.
   */
  submitPair = async () => {
    const partner = this.picked.partner;
    if (!partner) return this.#focusFirst('partner');
    if (partner.dataset.available === 'false') return this.#notify(partner);
    await this.#add([partner], this.refs.both);
  };

  /**
   * @param {HTMLButtonElement[]} chips
   * @param {HTMLElement} button
   * @param {HTMLButtonElement} [failed]
   */
  async #add(chips, button, failed) {
    const lines = chips.map((c) => ({ merchandiseId: String(c.dataset.variantId), quantity: 1 }));
    button.setAttribute('aria-busy', 'true');

    try {
      await addLines(this, lines, { productId: chips[0].dataset.variantId });
      this.#confirm(chips, failed);
    } catch {
      this.#showError();
    } finally {
      button.removeAttribute('aria-busy');
    }
  }

  /**
   * Say what went in. A drawer sliding open is not the same as being told the
   * size you chose is the size that was taken.
   * @param {HTMLButtonElement[]} chips
   * @param {HTMLButtonElement} [failed]
   */
  #confirm(chips, failed) {
    const name = (c) => `${c.dataset.pieceName} ${c.dataset.sizeLabel}`;
    let text;
    if (failed) {
      text = (this.dataset.labelAddedPartial || '{ok} was added. {failed} could not be.')
        .replace('{ok}', name(chips[0]))
        .replace('{failed}', name(failed));
    } else if (chips.length > 1) {
      text = `${name(chips[0])} and ${name(chips[1])} added to your bag`;
    } else {
      text = `${name(chips[0])} added to your bag`;
    }
    this.#message(text);
  }

  /* -------------------------------------------------------- back in stock */

  /** @param {HTMLButtonElement} chip */
  #notify(chip) {
    const here = this.#isThisPage(chip);
    const trigger = here
      ? this.querySelector('[data-lea-bis] a, [data-lea-bis] button, [data-lea-bis] [role="button"]')
      : null;
    if (trigger instanceof HTMLElement) return trigger.click();

    const base = here ? window.location.href : new URL(chip.dataset.pieceUrl ?? '', window.location.origin).toString();
    const url = new URL(base, window.location.origin);
    url.searchParams.set('variant', String(chip.dataset.variantId));
    url.searchParams.set('lea_bis', '1');
    window.location.assign(url.toString());
  }

  /** @param {HTMLButtonElement | null} chip */
  #isThisPage(chip) {
    const url = chip?.dataset.pieceUrl;
    if (!url) return true;
    return window.location.pathname.startsWith(url.split('?')[0]);
  }

  #resumeNotify() {
    const url = new URL(window.location.href);
    if (url.searchParams.get('lea_bis') !== '1') return;

    const id = url.searchParams.get('variant');
    const chip = id && this.querySelector(`[data-variant-id="${id}"]`);
    if (chip instanceof HTMLButtonElement) chip.click();

    url.searchParams.delete('lea_bis');
    window.history.replaceState({}, '', url.toString());

    const anchor = this.querySelector('[data-lea-bis]');
    if (!anchor) return;
    const observer = new MutationObserver(() => {
      const trigger = anchor.querySelector('a, button, [role="button"]');
      if (!(trigger instanceof HTMLElement)) return;
      observer.disconnect();
      trigger.click();
    });
    observer.observe(anchor, { childList: true, subtree: true });
    setTimeout(() => observer.disconnect(), 8000);
  }

  /* ------------------------------------------------------------- plumbing */

  /** @param {'main' | 'partner'} role */
  #focusFirst(role) {
    const first = this.querySelector(`[data-lea-variant="${role}"][data-available="true"]`);
    if (first instanceof HTMLElement) first.focus();
  }

  /** @param {string} text */
  #message(text) {
    const slot = this.querySelector('[data-lea-error]');
    if (slot) {
      slot.textContent = text;
      slot.dataset.tone = 'ok';
    }
  }

  #clearMessage() {
    const slot = this.querySelector('[data-lea-error]');
    if (slot) {
      slot.textContent = '';
      delete slot.dataset.tone;
    }
  }

  #showError() {
    const slot = this.querySelector('[data-lea-error]');
    if (slot) {
      slot.textContent = this.dataset.labelError ?? 'That did not add. Try again.';
      slot.dataset.tone = 'bad';
    }
  }

  /** @param {'main'|'partner'} role */
  #stockLine(role) {
    const chip = this.picked[role];
    const slot = this.querySelector(`[data-lea-stock="${role}"]`);
    if (!(slot instanceof HTMLElement)) return;
    const left = chip?.dataset.scarce;
    if (chip && left) {
      const single = this.querySelectorAll(`[data-lea-variant="${role}"]`).length === 1;
      const template = single
        ? this.dataset.labelStockOne || 'Only __N__ left'
        : this.dataset.labelStock || 'Only __N__ left in __S__';
      slot.textContent = template.replace('__N__', left).replace('__S__', chip.dataset.sizeLabel || '');
      slot.hidden = false;
    } else {
      slot.hidden = true;
    }
  }

  #sync() {
    const main = this.picked.main;
    const partner = this.picked.partner;

    // The primary action speaks for the piece she came for, and nothing else.
    let label = this.dataset.labelAdd;
    let ready = true;
    if (!main) {
      ready = false;
      label = this.dataset.labelChooseMain;
    } else if (main.dataset.available === 'false') {
      label = this.dataset.labelNotify;
    }
    this.refs.submit.textContent = label ?? '';
    this.refs.submit.dataset.ready = String(ready);

    if (this.refs.both) {
      const bothReady = Boolean(this.picked.partner);
      this.refs.both.dataset.ready = String(bothReady);
      this.refs.both.textContent = this.dataset.labelAddBoth ?? '';
    }

    // The combined figure appears once both halves are chosen, so the maths is
    // shown rather than asserted up front.
    const total = this.querySelector('[data-lea-total]');
    if (total instanceof HTMLElement) total.hidden = !(main && partner);

    this.#mirror();
    this.#stockLine('main');
    this.#stockLine('partner');
  }
}

if (!customElements.get('lea-set-builder')) {
  customElements.define('lea-set-builder', LeaSetBuilder);
}
